/**
 * Knowledge Base — in-app documentation for the whole team. Every page /
 * metric / job the platform runs is documented here with four consistent
 * facets: What it measures, When it's crawled, Where it's stored, and
 * What good looks like (benchmark).
 *
 * Data-driven — everything lives in the `SECTIONS` array so adding a new
 * topic is one object literal, not a new component. Rendered as a single
 * scrollable page with a sticky left rail for jump-to-section and a search
 * box at the top that filters sections by title / body.
 */
import React, { useMemo, useState } from "react";
import { BookOpen, Search as SearchIcon, Clock, Database, Target, Info } from "lucide-react";

/* ─────────────────────────── SECTION DATA ─────────────────────────── */

const SECTIONS = [
  {
    id: "overview",
    title: "Overview",
    lead: "How the platform pulls, stores and surfaces SEO data.",
    body: (
      <>
        <p>
          rank.os is a multi-client SEO dashboard. Every client gets a portfolio of tracked keywords, backlink metrics, competitor comparisons,
          Search Console + Google Analytics 4 integration, PageSpeed audits, and an AI-generated monthly briefing.
        </p>
        <p>
          Data comes from four providers wired to background schedulers so the app is never live-querying expensive APIs on page load:
        </p>
        <ul>
          <li><strong>DataForSEO</strong> — SERP, keyword volumes, backlinks, competitor domains, Local Pack / Local Finder.</li>
          <li><strong>Google Search Console + GA4</strong> — organic clicks, impressions, sessions, conversions (via OAuth).</li>
          <li><strong>Google PageSpeed Insights</strong> — Core Web Vitals for the top landing pages.</li>
          <li><strong>Moz API v2</strong> — Domain Authority, Page Authority, Spam Score (a second opinion alongside DataForSEO&apos;s Domain Rank).</li>
        </ul>
        <p>Cached values that expire are refreshed automatically by the scheduler; live-only endpoints are flagged in the tab header of the corresponding page.</p>
      </>
    ),
  },

  {
    id: "adding-a-client",
    title: "Adding a New Client",
    lead: "Field-by-field walkthrough of the New Client form. All fields except keywords can be edited later.",
    body: (
      <>
        <p>
          Go to <strong>Clients → + New client</strong>. The form is organised into 5 collapsible sections. Fields marked <span className="font-mono-tight text-[#c53030]">*</span> are required to save.
        </p>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">1. Client Identity</h4>
        <dl>
          <dt><strong>Client logo</strong> — optional</dt>
          <dd>PNG or JPG, ideally square, ≤2MB. Appears on the PDF report cover and in the client list. If missing, we fall back to a coloured initial.</dd>
          <dt><strong>Client name</strong> <span className="font-mono-tight text-[#c53030]">*</span></dt>
          <dd>The internal / display name your team will search by. Doesn&apos;t need to match legal or trading name — this is for you.</dd>
          <dt><strong>Primary domain</strong> <span className="font-mono-tight text-[#c53030]">*</span></dt>
          <dd>The domain you&apos;re optimising, without protocol or trailing slash — e.g. <span className="font-mono-tight">delany-motors.co.uk</span>. Both <span className="font-mono-tight">example.com</span> and <span className="font-mono-tight">www.example.com</span> are accepted; the matcher normalises `www.` automatically.</dd>
          <dt><strong>Country</strong> <span className="font-mono-tight text-[#c53030]">*</span></dt>
          <dd>Country-level DataForSEO location (e.g. <span className="font-mono-tight">United Kingdom</span>, <span className="font-mono-tight">United States</span>). This is the SERP your <em>national</em> keywords track against. <strong>Don&apos;t put a city here</strong> — an amber warning will flag it and offer a one-click fix.</dd>
          <dt><strong>Language</strong></dt>
          <dd>Defaults to <span className="font-mono-tight">English</span>. Change only for non-English markets (<span className="font-mono-tight">Spanish</span>, <span className="font-mono-tight">French</span>, etc.). This affects SERP language, not the dashboard UI.</dd>
        </dl>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">2. Campaign Settings</h4>
        <dl>
          <dt><strong>Campaign type</strong> — <span className="font-mono-tight">National</span> or <span className="font-mono-tight">Local</span></dt>
          <dd>The single most important switch on the form. Determines whether Local SEO features (Local Pack tracking, GBP integration, service-area SERPs, Local Finder heatmap) are enabled. See the &quot;Local vs National Setup&quot; section below for a full comparison.</dd>
          <dt><strong>Service location</strong> — <em>only shown when Local</em></dt>
          <dd>Full DFS geo-target format: <span className="font-mono-tight">City,Region,Country</span> — e.g. <span className="font-mono-tight">Stockport,England,United Kingdom</span>. This is the SERP your <em>local</em> keywords track against. Autocomplete from the DFS location index catches typos.</dd>
          <dt><strong>GBP business name</strong> — <em>only shown when Local</em></dt>
          <dd>The exact name the client&apos;s Google Business Profile uses. Used as a fuzzy-match fallback when we can&apos;t resolve them via CID. Copy-paste from Google Maps for best accuracy.</dd>
          <dt><strong>GBP CID</strong> — <em>only shown when Local, highly recommended</em></dt>
          <dd>
            Google&apos;s customer ID for the client&apos;s GBP listing. Most precise way to match them to Local Pack results.<br />
            <strong>How to find it:</strong> Open the client&apos;s Google Maps listing → copy the URL → find <span className="font-mono-tight">?cid=1234567890</span> in the URL. The number after <span className="font-mono-tight">cid=</span> is the CID. Paste just the number. Use the &quot;Test&quot; button to verify.
          </dd>
          <dt><strong>Hyper-local search radius</strong> — <em>only shown when Local</em></dt>
          <dd>How wide the Local Finder heatmap crawls around the GBP location. Options: 1km / 3km / 5km / 10km. Choose based on the client&apos;s realistic catchment: a coffee shop → 3km, a plumber → 10km. Wider radius = more DFS credit consumed per monthly run.</dd>
        </dl>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">3. Google Integrations <span className="text-[#a0aec0] font-mono-tight text-xs">(optional)</span></h4>
        <dl>
          <dt><strong>GA4 property ID</strong></dt>
          <dd>The 9-digit Property ID from Analytics (Admin → Property Settings). Format: just the digits, e.g. <span className="font-mono-tight">312345678</span>. Requires OAuth connection first — click &quot;Connect Google&quot; on the client dashboard. Use &quot;Test&quot; to verify permission before saving.</dd>
          <dt><strong>Search Console property</strong></dt>
          <dd>The verified property URL as it appears in GSC — <span className="font-mono-tight">https://example.com/</span> (URL-prefix) or <span className="font-mono-tight">sc-domain:example.com</span> (Domain property). Click &quot;Load sites&quot; to pick from a list of everything your OAuth account has access to.</dd>
        </dl>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">4. Keywords & Internal Notes</h4>
        <dl>
          <dt><strong>National keywords</strong> — one per line</dt>
          <dd>Terms polled at the country-level SERP set in Country. Max 100 total across both lists. Duplicates within a list are auto-deduplicated on save.</dd>
          <dt><strong>Local keywords</strong> — <em>only shown when Local</em></dt>
          <dd>Terms polled at the city-level SERP set in Service location. Same 100-max applies across both lists. Paste a mixed list and use the &quot;N look local&quot; / &quot;N look national&quot; scope-hint banners to auto-sort them.</dd>
          <dt><strong>Internal notes</strong> — never surfaced in client-facing reports</dt>
          <dd>Free-text scratchpad for the account manager. Common uses: content-plan ideas, backlink outreach notes, client preferences, invoice reminders. Only visible to logged-in users.</dd>
        </dl>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">5. Reports</h4>
        <dl>
          <dt><strong>Client first name</strong></dt>
          <dd>Used to personalise the monthly digest email greeting — &quot;Hi Sarah,&quot; instead of &quot;Hi there,&quot;. Just the first name.</dd>
          <dt><strong>Report email</strong></dt>
          <dd>Recipient(s) of the monthly PDF digest. Multiple emails supported — separate by commas. All are Bcc&apos;d, so recipients can&apos;t see each other. Leave blank to skip the digest for this client.</dd>
          <dt><strong>Assigned user</strong></dt>
          <dd>The team member responsible for this client. Members only see clients assigned to them (admins see everything). Used for filtering on the Clients list and for future account-manager exports.</dd>
        </dl>

        <p className="mt-5 text-[12px] text-[#718096] italic">
          After saving, the first rank check runs on the next Monday at 09:00 UTC. Use the &quot;Check ranks now&quot; button on the Rank Tracking tab to trigger one immediately.
        </p>
      </>
    ),
  },

  {
    id: "local-vs-national-setup",
    title: "Local vs National Setup",
    lead: "Full comparison of what each campaign type unlocks — pick the right one when adding a client.",
    body: (
      <>
        <p>The <strong>Campaign type</strong> switch on the Client form does far more than toggle a tag — it enables or hides entire features. Here&apos;s the full breakdown:</p>

        <div className="overflow-x-auto my-4">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#e2e8f0]">
                <th className="py-2 pr-4">Feature</th>
                <th className="py-2 pr-4">National</th>
                <th className="py-2">Local</th>
              </tr>
            </thead>
            <tbody className="text-[#4a5568]">
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">Keyword lists</td>
                <td className="py-2 pr-4">National only (single list)</td>
                <td className="py-2">National <em>and</em> Local (two lists)</td>
              </tr>
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">SERP location for keywords</td>
                <td className="py-2 pr-4">Country only (e.g. UK)</td>
                <td className="py-2">National kw → Country · Local kw → City</td>
              </tr>
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">Local Pack tracking</td>
                <td className="py-2 pr-4"><span className="text-[#a0aec0]">Off</span></td>
                <td className="py-2 text-[#38b2ac] font-semibold">On</td>
              </tr>
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">GBP business match</td>
                <td className="py-2 pr-4"><span className="text-[#a0aec0]">Off</span></td>
                <td className="py-2 text-[#38b2ac] font-semibold">On (via CID / name)</td>
              </tr>
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">Local Finder heatmap</td>
                <td className="py-2 pr-4"><span className="text-[#a0aec0]">Off</span></td>
                <td className="py-2 text-[#38b2ac] font-semibold">On (monthly, gated)</td>
              </tr>
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">Review-velocity tracking</td>
                <td className="py-2 pr-4"><span className="text-[#a0aec0]">Off</span></td>
                <td className="py-2 text-[#38b2ac] font-semibold">On (via GBP)</td>
              </tr>
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">Local SEO tab in dashboard</td>
                <td className="py-2 pr-4"><span className="text-[#a0aec0]">Hidden</span></td>
                <td className="py-2 text-[#38b2ac] font-semibold">Visible</td>
              </tr>
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">PDF report sections</td>
                <td className="py-2 pr-4">Rankings, Backlinks, GA4/GSC, CWV, Competitors</td>
                <td className="py-2">Above + Local Pack + Local Finder + Reviews</td>
              </tr>
              <tr className="border-b border-[#edf2f7]">
                <td className="py-2 pr-4 font-semibold">Required extra fields</td>
                <td className="py-2 pr-4">None</td>
                <td className="py-2">Service location, GBP name (CID recommended)</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-semibold">DFS credit cost / month</td>
                <td className="py-2 pr-4">~1 SERP call × 100 kw = 100 credits</td>
                <td className="py-2">Above + Local Pack + heatmap ≈ 250–400 credits</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">When to pick National</h4>
        <ul>
          <li><strong>Ecommerce sites</strong> selling nationwide (or globally) — customers don&apos;t care where you&apos;re based.</li>
          <li><strong>SaaS / digital products</strong> — no physical service area.</li>
          <li><strong>National brand terms</strong> where the business isn&apos;t tied to a specific town (e.g. a directory site, blog, content publisher).</li>
          <li><strong>Franchise headquarters</strong> — track the master brand nationally; individual franchise locations get their own Local clients.</li>
        </ul>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">When to pick Local</h4>
        <ul>
          <li><strong>Service-area businesses</strong> — plumbers, electricians, dentists, garages, restaurants, salons, gyms. Anything where the customer has to physically visit or the service is delivered to a specific area.</li>
          <li><strong>Multi-location businesses</strong> — one Local client <em>per location</em>, each with its own Service location + GBP CID. Track each independently.</li>
          <li><strong>Any business with a Google Business Profile</strong> that you want to track for Local Pack rankings.</li>
        </ul>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">Hybrid: Local client tracking a few national terms</h4>
        <p>A Local client can also have National keywords (e.g. a Stockport garage tracking &quot;car servicing&quot; nationally to spot broader trends). Just put those in the National keywords list — they&apos;ll track at the country SERP while the local list tracks at Stockport. This is why the client form always shows both lists when Campaign = Local.</p>

        <h4 className="font-display text-base mt-5 mb-1 text-[#1a202c]">Common mistakes</h4>
        <ul>
          <li><strong>Typing a city in the Country field</strong> — sets your national SERP to a hyperlocal crawl instead of country-wide. The form catches this with an amber warning.</li>
          <li><strong>Skipping GBP CID</strong> on a Local client — the app falls back to fuzzy name matching, which is less reliable when there are similar-named businesses in the same town.</li>
          <li><strong>Putting local-intent keywords in the National list</strong> — you&apos;ll get country-wide SERPs which usually don&apos;t reflect the client&apos;s real local ranking. The scope-hint banner catches these on entry.</li>
          <li><strong>Wrong Service location format</strong> — it must be <span className="font-mono-tight">City,Region,Country</span>, not <span className="font-mono-tight">City, Region, Country</span> (no spaces after commas) or just <span className="font-mono-tight">City</span> alone.</li>
        </ul>
      </>
    ),
  },

  {
    id: "rank-tracking",
    title: "Rank Tracking",
    lead: "Where your tracked keywords sit on Google, week after week.",
    what: "For each tracked keyword we call the DataForSEO Google Organic SERP endpoint (top 20 organic + Local Pack) and record the position of the client's domain. Every result row carries scope (national/local), position, url, search volume, and local_pack_position if the client's business appears in the map pack.",
    when: (
      <>
        <strong>Weekly:</strong> every Monday at 09:00 UTC (all clients, all keywords).<br />
        <strong>Monthly:</strong> re-run on the 1st of every month at 10:30 UTC (reuses a rank check from the last 3 days if one exists) — feeds the PDF digest.<br />
        <strong>On demand:</strong> the &quot;Check ranks now&quot; button on any client&apos;s Rank Tracking tab.
      </>
    ),
    stored: "Every run produces a rank_snapshots document keyed by (client_id, at). Snapshots are kept indefinitely — historical charts read the full series. Deleting a client doesn't purge historical snapshots automatically.",
    good: (
      <ul>
        <li><strong>Search Visibility</strong> (% of tracked keywords in top 10): <span className="text-[#38b2ac] font-semibold">60%+ = strong</span>, 30–60% = growing, under 30% = early stage.</li>
        <li><strong>Top 3 %</strong>: aim for 30%+ over the medium term — top-3 is where 70% of clicks happen.</li>
        <li><strong>Local Pack presence</strong>: for local clients, appearing in the Local Pack for 60%+ of tracked local keywords is healthy.</li>
        <li><strong>Week-over-week movement</strong>: ±2 positions per keyword is normal noise. A drop of 5+ across the whole portfolio is a signal to investigate.</li>
      </ul>
    ),
  },

  {
    id: "national-vs-local",
    title: "National vs Local Keywords",
    lead: "Split-scope tracking — one client can be tracked at both country and city SERPs.",
    what: "Each client has two keyword lists: National (polled at the country-level SERP set in Country) and Local (polled at the city-level SERP set in Service Location, only shown when Campaign type = Local). The same keyword can appear in both lists — it will be crawled twice (once nationally, once locally) so you can compare 'nationwide vs in-town' rankings.",
    when: <>Same cadence as Rank Tracking — weekly Monday, monthly 1st.</>,
    stored: "Each rank_snapshots.results[] row carries a scope tag ('national' or 'local'). Legacy pre-scope-split rows fall back to campaign_type for filter behaviour.",
    good: (
      <ul>
        <li><strong>Country:</strong> Should always be a country like &quot;United Kingdom&quot; or &quot;United States&quot; — not a city. If typed as a city, an amber warning appears in the Edit Client modal.</li>
        <li><strong>Service Location:</strong> Full DFS geo-target format — <span className="font-mono-tight">City,Region,Country</span> (e.g. <span className="font-mono-tight">Stockport,England,United Kingdom</span>).</li>
        <li><strong>Choose local scope when</strong> the keyword contains a place name (&quot;dentist bromsgrove&quot;), a &quot;near me&quot;-style modifier, or the client&apos;s core business is service-area (plumber, dentist, garage, restaurant, etc.).</li>
        <li><strong>Choose national scope when</strong> the keyword is broad, brand-based, or ecommerce (&quot;buy running shoes&quot;, &quot;seo dashboard&quot;).</li>
      </ul>
    ),
  },

  {
    id: "backlinks",
    title: "Backlinks",
    lead: "Off-page authority — who links to you, how many, and how healthy those links look.",
    what: (
      <>
        <p>Every backlink metric on the Backlinks tab comes from the <strong>DataForSEO Backlinks API</strong>, cross-referenced with <strong>Moz DA/PA</strong> for a second opinion.</p>
        <dl>
          <dt><strong>Backlinks</strong></dt>
          <dd>Total number of live links pointing to your domain. A single referring page can contribute multiple backlinks (e.g. sitewide footer link on 500 pages = 500 backlinks).</dd>
          <dt><strong>Ref Domains</strong></dt>
          <dd>Count of unique domains with at least one live link to you. 100 links from 100 sites is far stronger than 10,000 links from one site.</dd>
          <dt><strong>Ref IPs</strong></dt>
          <dd>Count of unique IP addresses hosting the referring domains. A low ref-IPs to ref-domains ratio suggests a PBN (private blog network) — a Google spam signal.</dd>
          <dt><strong>Domain Rank</strong></dt>
          <dd>DataForSEO&apos;s proprietary authority score, <span className="font-mono-tight">0–1000 scale</span>. Different from Moz DR/DA — see the &quot;Metric scales&quot; note under &quot;What good looks like&quot;.</dd>
          <dt><strong>Top Anchors</strong></dt>
          <dd>The <em>clickable text</em> of your inbound links (not the pages). E.g. &quot;Delany Motors&quot; used 42 times, &quot;click here&quot; used 8 times. Reveals whether your link profile looks natural or over-optimised.</dd>
          <dt><strong>Moz DA / PA / Spam Score</strong></dt>
          <dd>Moz&apos;s 0–100 Domain Authority, Page Authority, and a 0–17 Spam Score. Different index from DFS — treat both as opinions, not truths.</dd>
        </dl>
      </>
    ),
    when: (
      <>
        <strong>Monthly:</strong> on the 1st at 10:30 UTC as part of the monthly job.<br />
        <strong>On-demand:</strong> opening the Backlinks tab triggers a live DFS call if the cached snapshot is over 30 days old.
      </>
    ),
    stored: "Backlinks summary is fetched fresh each page load (DFS has near-real-time indexing). Moz responses are cached for 30 days in moz_cache to control cost.",
    good: (
      <>
        <ul>
          <li><strong>Ref Domains growth:</strong> +5–20 new domains per month for an SMB, +50+ for an ecom / content site is healthy.</li>
          <li><strong>Ref IPs / Ref Domains ratio:</strong> 0.6–0.9 is healthy. Under 0.3 suggests a PBN footprint — Google usually penalises.</li>
          <li><strong>Domain Rank progression:</strong>
            <ul>
              <li><span className="font-mono-tight">0–50</span> = brand new / very weak</li>
              <li><span className="font-mono-tight">200–400</span> = typical SMB</li>
              <li><span className="font-mono-tight">500–700</span> = established SME</li>
              <li><span className="font-mono-tight">800+</span> = strong authority</li>
            </ul>
          </li>
          <li><strong>Moz Spam Score:</strong> under 5 is fine, 5–10 acceptable, 10+ investigate the link profile for toxic domains.</li>
          <li><strong>Anchor text mix:</strong> 50–70% branded, 10–20% naked URL, 10–20% generic (&quot;click here&quot; / &quot;read more&quot;), 5–10% exact-match keyword. Over ~10% keyword anchors is a Penguin risk.</li>
        </ul>
        <p className="mt-3 text-[11px] text-[#718096]">
          <strong>Metric scales don&apos;t map linearly:</strong> Domain Rank 800 ≠ Moz DA 80. DFS uses 0–1000, Moz and Ahrefs use 0–100. Keep both scores as independent signals.
        </p>
      </>
    ),
  },

  {
    id: "keyword-research",
    title: "Keyword Research",
    lead: "Discover new terms with search-volume and difficulty data.",
    what: "Live DataForSEO Labs endpoint returning keyword suggestions with monthly search volume, keyword difficulty (0–100), CPC estimate, and competition intensity. Also supports 'seed + related' expansions.",
    when: "Live only — every search hits DFS fresh. Nothing cached.",
    stored: "Not persisted. Results are ephemeral — use the export function if you need them for a document.",
    good: (
      <ul>
        <li><strong>Search Volume:</strong> for local terms, 50+ monthly searches is worth tracking. For national terms, 500+ is a viable target for content.</li>
        <li><strong>Keyword Difficulty:</strong> under 30 = easy to rank with light optimisation. 30–60 = needs content + backlinks. 60+ = only viable for high-authority sites.</li>
        <li><strong>CPC signal:</strong> higher CPC = higher commercial intent. A £8 CPC keyword with 200 volume is often more valuable than a £0.30 CPC keyword with 2000 volume.</li>
      </ul>
    ),
  },

  {
    id: "serp-analysis",
    title: "SERP Analysis",
    lead: "Live snapshot of the top-20 organic results for any keyword.",
    what: "Fires a fresh DataForSEO Google Organic SERP call and renders the top 20 results with title, URL, meta, position, and a 'YOU' badge on your own domain if present. Also surfaces related terms and questions.",
    when: "Live only — every Analyze click hits DFS fresh.",
    stored: "Not persisted. Use the client's Rank Tracking tab for stored historical positions.",
    good: (
      <ul>
        <li>Use this tab to <strong>spot-check</strong> a specific keyword before adding it to tracking, or when a client asks &quot;why did we drop?&quot;.</li>
        <li><strong>SERP location:</strong> Uses the client&apos;s <em>Country</em> field, not Service Location. If you need a hyperlocal live check, use the standalone <span className="font-mono-tight">/tools/serp</span> tool with a manual location.</li>
      </ul>
    ),
  },

  {
    id: "site-xray",
    title: "Site X-Ray",
    lead: "24-month Ahrefs history on any domain — Domain Rating, referring domains, organic traffic, SEO value, organic pages, and keyword position buckets.",
    what: (
      <>
        Standalone tool at <span className="font-mono-tight">/tools/site-xray</span> that fans out five parallel Ahrefs v3 Site Explorer calls (<span className="font-mono-tight">domain-rating-history</span>, <span className="font-mono-tight">refdomains-history</span>, <span className="font-mono-tight">metrics-history</span>, <span className="font-mono-tight">pages-history</span>, <span className="font-mono-tight">keywords-history</span>) and renders six KPI cards plus six trend charts:
        <ul>
          <li><strong>Authority &amp; Organic Traffic</strong> — DR + estimated monthly sessions (dual-axis line)</li>
          <li><strong>Referring Domains</strong> — cumulative total linking sites (area chart)</li>
          <li><strong>Net new refdomains</strong> — month-over-month delta derived from the cumulative series (bar chart). Hides churn: 10 gained + 10 lost = net 0. Ahrefs doesn&apos;t expose per-month new/lost directly.</li>
          <li><strong>SEO traffic value (£/month)</strong> — what the organic clicks would cost on Google Ads. Ahrefs returns USD cents; converted here at $0.79/£.</li>
          <li><strong>Organic pages</strong> — URLs ranking in top 100 globally. Rising = content investment paying off; sudden drop = indexation / penalty signal.</li>
          <li><strong>Organic positions by bucket</strong> — stacked bars: top 3 / 4-10 / 11-20 / 21-50 / 51+</li>
        </ul>
      </>
    ),
    when: "On-demand only — click Scan with a domain. 28-day Mongo cache per (target, country, months) tuple means repeat lookups are free; a Force refresh button bypasses the cache when you want fresh numbers.",
    stored: (
      <>
        Each successful scan logs real spend to <span className="font-mono-tight">ahrefs_logs</span> and the result payload to <span className="font-mono-tight">ahrefs_cache</span>. Month-to-date unit spend surfaces as a badge in the page header and on <span className="font-mono-tight">/api/status</span> alongside DFS spend.
      </>
    ),
    good: (
      <ul>
        <li><strong>Not tied to the clients list.</strong> Scout competitors, prospects, random lookups — no need to create a client first.</li>
        <li><strong>Default to 24 months.</strong> 6/12/24/36/60 selectable. 24 months is the sweet spot for seeing one-off algo-update dips without drowning the chart in noise.</li>
        <li><strong>Country codes are lowercase</strong> (Ahrefs is case-sensitive). Dropdown shows friendly uppercase labels; backend handles the conversion.</li>
        <li><strong>Cost per cold scan ≈ 1,250 units</strong> for 24 months. Lite plan&apos;s 200K/mo allowance gives you ~160 fresh scans. Repeat scans of the same combo for 28 days are free (served from cache).</li>
        <li><strong>Interpretation tips:</strong>
          <ul>
            <li>A flat <em>Referring Domains</em> line can hide churn — always sanity-check against the <em>Net new refdomains</em> chart below it.</li>
            <li>Big <em>Organic pages</em> drop with flat DR = indexation / crawl issue. Big drop with falling DR = algo penalty.</li>
            <li>SEO value trending up while DR flat = you&apos;re winning higher-intent (commercial) keywords. Classic sign of landing-page work paying off.</li>
          </ul>
        </li>
        <li><strong>Requires <span className="font-mono-tight">AHREFS_API_KEY</span></strong> in <span className="font-mono-tight">backend/.env</span>. Create one at <a className="text-[#2c7a7b] underline" href="https://app.ahrefs.com/account/api/profile" target="_blank" rel="noreferrer">app.ahrefs.com → Account → API</a>. Keys expire after 1 year.</li>
      </ul>
    ),
  },

  {
    id: "competitors",
    title: "Competitors",
    lead: "Share-of-voice across your tracked keywords vs your top 3 competitor domains.",
    what: "For each keyword the client tracks, we identify the top 10 competing domains from the SERP and rank how often each competitor appears vs the client. Produces a share-of-voice score (0–100%) and a per-keyword rank comparison table.",
    when: (
      <>
        <strong>Monthly:</strong> on the 1st as part of the monthly job.<br />
        <strong>On-demand:</strong> &quot;Refresh competitors&quot; button on the Competitors tab.
      </>
    ),
    stored: "comparison_snapshots collection — one row per (client_id, at). TTL: 12 months. Historical timeline uses the full series.",
    good: (
      <ul>
        <li><strong>Share of voice:</strong> 25%+ vs your top 3 competitors is a strong position. Under 10% means you&apos;re losing more SERPs than you&apos;re winning.</li>
        <li><strong>Trend matters more than absolute value:</strong> a growing SoV over 3+ months is more predictive of future traffic than a snapshot number.</li>
        <li><strong>Watch new entrants:</strong> a domain you&apos;ve never seen before hitting your top-3 competitor list means Google has boosted them — investigate their content / backlink strategy.</li>
      </ul>
    ),
  },

  {
    id: "local-seo",
    title: "Local SEO / GBP",
    lead: "Google Business Profile visibility, Local Pack rankings, and neighbourhood-grid Local Finder.",
    what: (
      <>
        <p>Only shown for clients with <strong>Campaign type = Local</strong>. Combines three data sources:</p>
        <ul>
          <li><strong>Local Pack position</strong> per tracked keyword (from the weekly SERP crawl) — 1, 2, 3 or off-pack.</li>
          <li><strong>Local Finder heatmap</strong> — a grid of coordinates around the client&apos;s GBP, each queried for the tracked keyword, showing rank at each point. Reveals whether you&apos;re only visible at your storefront or across the whole town.</li>
          <li><strong>GBP metadata</strong> — total reviews, average rating, review velocity delta, photo count, category. Matched to the client via CID (best), business-name fuzzy match, or domain (fallback).</li>
        </ul>
      </>
    ),
    when: (
      <>
        <strong>Local Pack:</strong> weekly with rank tracking.<br />
        <strong>Local Finder heatmap:</strong> monthly on the 1st (expensive DFS call, gated to control cost).<br />
        <strong>GBP metadata:</strong> refreshed with every rank check when a matching pack listing is found.
      </>
    ),
    stored: "local_finder_snapshots collection (TTL 12 months). GBP CID + coordinates are stored on the client record so the search radius can be centred on the actual business, not the town centroid.",
    good: (
      <ul>
        <li><strong>Local Pack presence:</strong> appearing in top-3 for 60%+ of tracked local keywords is strong.</li>
        <li><strong>Local Finder coverage:</strong> ranking in top-3 across 40%+ of the heatmap grid = healthy geographic reach. Under 20% means you&apos;re only visible near your storefront.</li>
        <li><strong>Review velocity:</strong> +3–5 new reviews per month is normal for an SMB. Big spikes can help Local Pack ranking but Google&apos;s spam filters flag suspicious bursts.</li>
        <li><strong>Response rate:</strong> replying to reviews (positive AND negative) within 48h is a small ranking signal and a big trust signal to prospects.</li>
      </ul>
    ),
  },

  {
    id: "ga4",
    title: "Google Analytics 4",
    lead: "Sessions, users, conversions, engagement — from the connected GA4 property.",
    what: "Server-to-server calls to the GA4 Data API via the client's OAuth-connected Google account. Returns period-over-period metrics for the selected range (28d / 30d / 60d / 90d / YTD), plus a Top Landing Pages table.",
    when: (
      <>
        <strong>On demand + cached 24h:</strong> opening a client&apos;s GA4 panel serves the cached payload if under 24h old, otherwise refreshes. Manual refresh available via the range toggle.
      </>
    ),
    stored: "ga4_cache collection with a compound unique index on (property_id, range_key). TTL: 24 hours. Each range caches independently so switching between 28d/60d/90d doesn't invalidate the others.",
    good: (
      <ul>
        <li><strong>Organic sessions growth:</strong> +10% year-over-year is healthy for an established site. New sites should see +50–100% Y/Y in year 1.</li>
        <li><strong>Engagement rate:</strong> 55%+ is good. Under 40% suggests content-to-query mismatch.</li>
        <li><strong>Conversion rate:</strong> service-area businesses: 3–5% typical. Ecommerce: 1.5–3%. Below 1% investigate landing page / form / CTA.</li>
        <li><strong>Top landing pages:</strong> if your homepage is your top page &gt;60% of the time, you&apos;re missing long-tail content opportunities.</li>
      </ul>
    ),
    caveat: "OAuth refresh tokens expire every 7 days when the Google Cloud consent screen is in Testing status. Publishing the consent screen (Production status) removes this limit. See the GA4 status banner on the client dashboard for a live token-health check.",
  },

  {
    id: "search-console",
    title: "Search Console",
    lead: "Impressions, clicks, CTR, and average position for organic queries you actually rank for.",
    what: "GSC Search Analytics API queried for the last 28 days vs the previous 28 days. Surfaces four panels: Overview KPIs, Top Queries, Top Pages, and Striking Distance queries (positions 4–15 — the ones closest to top-3).",
    when: (
      <>
        <strong>On demand + cached 24h:</strong> same cadence as GA4. Data has a built-in 2-day reporting lag (Google&apos;s own) so &quot;yesterday&quot; is never available.
      </>
    ),
    stored: "gsc_cache collection with a compound unique index on (site_url, kind). TTL: 24 hours. Kinds: 'overview_28d' and 'striking_distance' cached separately.",
    good: (
      <ul>
        <li><strong>Average position vs Rank Tracking position:</strong> GSC position is an average across all impression contexts (device, location, personalisation), so it&apos;s always &quot;muddier&quot; than the clean rank-tracked position. A difference of 2–5 is normal.</li>
        <li><strong>CTR at rank:</strong> position 1 = 25–35% CTR is typical. Position 5 = 5–8%. Position 10 = 1–3%. Rows underperforming these ranges suggest a weak title/meta description.</li>
        <li><strong>Striking Distance queries</strong> (positions 4–15) are your highest-ROI content-tweak targets. A small on-page improvement can push them into top-3.</li>
      </ul>
    ),
  },

  {
    id: "page-insights",
    title: "Page Insights (Core Web Vitals)",
    lead: "PageSpeed audits for your top-10 GSC landing pages, mobile + desktop.",
    what: "Runs Google PageSpeed Insights API for each of the top-10 landing pages (sorted by GSC clicks), mobile + desktop. Records LCP, INP, CLS, TBT, FCP, TTFB, and the overall Performance score.",
    when: "Weekly Tuesday at 09:00 UTC. Also runs on-demand via the &quot;Re-audit&quot; button.",
    stored: "pagespeed_snapshots collection. TTL: 6 months (Core Web Vitals thresholds evolve, older scans lose signal).",
    good: (
      <ul>
        <li><strong>LCP</strong> (Largest Contentful Paint): under 2.5s = good, 2.5–4s = needs improvement, over 4s = poor.</li>
        <li><strong>INP</strong> (Interaction to Next Paint): under 200ms = good, 200–500ms = needs improvement.</li>
        <li><strong>CLS</strong> (Cumulative Layout Shift): under 0.1 = good, 0.1–0.25 = needs improvement.</li>
        <li><strong>Performance score:</strong> 90+ = good, 50–89 = needs improvement, under 50 = poor. Mobile scores are always lower than desktop — compare within-device, not across.</li>
        <li><strong>Impact on rankings:</strong> CWV is a real but modest ranking factor. Failing all three (LCP/INP/CLS) can suppress rankings by 5–10 positions; passing all three doesn&apos;t magically boost you — it&apos;s a hygiene bar.</li>
      </ul>
    ),
  },

  {
    id: "ai-insights",
    title: "AI Insights",
    lead: "Claude Opus-generated monthly briefing that reads all your data and writes the story.",
    what: (
      <>
        <p>Once per calendar month per client, Claude Opus 4.8 reads a compiled brief and produces:</p>
        <ul>
          <li><strong>Executive summary</strong> — 3–4 sentence &quot;story of the month&quot;, also embedded on the PDF cover.</li>
          <li><strong>Wins / Concerns / Opportunities</strong> — three columns of narrative bullets.</li>
          <li><strong>Recommended actions</strong> — 5–8 concrete tasks tagged Quick / Medium / Deep effort.</li>
        </ul>
      </>
    ),
    when: (
      <>
        <strong>Automatic:</strong> 1st of every month at 10:30 UTC, after the monthly rank + competitor + digest jobs finish.<br />
        <strong>Auto-catch-up:</strong> if an admin opens the AI Insights tab and the cached briefing is from a prior month, generation is triggered on the fly.<br />
        <strong>Manual:</strong> &quot;Regenerate&quot; button — admin only. Overwrites the current month&apos;s briefing.
      </>
    ),
    stored: "ai_insights collection keyed on (client_id, period=YYYY-MM). Regeneration overwrites within the same month. Actions marked done or snoozed persist across regeneration.",
    good: (
      <>
        <p>Time windows the briefing sees:</p>
        <ul>
          <li><strong>Rank data:</strong> last ~8 weekly snapshots (≈2 months)</li>
          <li><strong>GA4 traffic:</strong> rolling 28 days vs previous 28 days</li>
          <li><strong>Search Console:</strong> rolling 28 days vs previous 28 days</li>
          <li><strong>Competitor share-of-voice:</strong> latest snapshot only (point-in-time)</li>
          <li><strong>Local Pack:</strong> latest Local Finder run only</li>
          <li><strong>GEO (AI Visibility):</strong> last 3 monthly snapshots — mention rate delta, avg position delta, per-platform breakdown, uncovered prompts</li>
          <li><strong>Brand Mentions:</strong> last 2 monthly snapshots — citation count delta, top-cited pages, discovery trend</li>
        </ul>
        <p className="mt-3">Because it&apos;s regenerated monthly, mid-month rank shifts won&apos;t appear until either the 1st or a manual regeneration. Check the freshness chips on the tab header to see how old the briefing is.</p>
        <p className="mt-2 text-[12px] text-[#718096]">GEO signals are given a deliberately <strong>small but rising priority</strong> — expect 1–2 GEO-related items per briefing rather than the AI section dominating. As LLMs eat more search volume, this weighting will increase.</p>
      </>
    ),
  },

  {
    id: "geo",
    title: "GEO (AI Visibility)",
    lead: "Generative Engine Optimization — how ChatGPT, Claude, Gemini & Perplexity answer the prompts you track.",
    what: (
      <>
        <p>GEO is the SEO discipline for the AI-answer era. Instead of tracking where you rank in Google&apos;s SERP, we track whether the major LLMs <em>mention</em> your brand or cite your domain when a real user asks a question in your category.</p>
        <p className="mt-2">You configure a list of natural-language prompts per client (up to 20) in the Edit Client screen — e.g. <span className="font-mono-tight">&quot;best HR consultancy in Surrey&quot;</span> — and each month we fire every prompt at every enabled LLM via DataForSEO&apos;s LLM Responses API. For every prompt × platform pair we capture:</p>
        <ul>
          <li><strong>Mentioned?</strong> — did the answer name your brand or cite your domain in its sources?</li>
          <li><strong>Position in answer</strong> — 1-based rank among all cited sources. #1 = top-cited (highest trust signal), no number = named in prose but no URL citation.</li>
          <li><strong>Sources cited</strong> — the full list of URLs the answer referenced, so you can see who is competing with you in each LLM.</li>
          <li><strong>Full answer text</strong> — expandable in the tab so you can read exactly what the LLM told the user.</li>
        </ul>
        <p className="mt-3"><strong>Two charts on the tab:</strong></p>
        <ul>
          <li><strong>Mention rate over time</strong> — % of prompt-model pairs that mentioned you, per snapshot. Higher is better. Bold black &quot;all platforms&quot; line + dashed per-platform lines so you can see which providers are moving.</li>
          <li><strong>Avg citation position over time</strong> — where you land among sources, averaged across all prompts where you were cited. Y-axis is <em>inverted</em> so #1 sits at the top and rising lines = improvement (matches how a client instinctively reads &quot;going up&quot;).</li>
        </ul>
      </>
    ),
    when: (
      <>
        <strong>Automatic:</strong> monthly cron on the 1st @ 10:30 UTC alongside rank tracking and the digest email. Silently no-ops for clients with no prompts or no enabled platforms — zero cost when opted out.<br />
        <strong>Manual:</strong> &quot;Check AI now&quot; button on the tab fires a fresh crawl on-demand and appends a snapshot.
      </>
    ),
    stored: "ai_visibility_snapshots collection — one document per crawl per client with the full results array (prompt × platform rows), platforms list, total cost, timestamp. Both charts read directly from this history.",
    good: (
      <>
        <p><strong>Reading the mention rate:</strong></p>
        <ul>
          <li><strong>0%</strong> — no LLM named you for any tracked prompt. Either you don&apos;t exist in their training data / retrieval index yet, or the prompts are too generic. Try more branded / niche prompts.</li>
          <li><strong>20–40%</strong> — you&apos;re on the radar. Focus content briefs on the prompts where 0 platforms mentioned you (see &quot;uncovered_prompts&quot; in the AI Insights briefing).</li>
          <li><strong>60%+</strong> — strong. You&apos;re a genuine authority in your topic cluster. Push for citation position #1–3 next.</li>
        </ul>
        <p className="mt-3"><strong>Reading avg citation position:</strong></p>
        <ul>
          <li><strong>#1–2 avg</strong> — you&apos;re the primary source most LLMs are trusting. Excellent.</li>
          <li><strong>#3–5 avg</strong> — you&apos;re in the mix. Look at who ranks above you in the sources list — those are your GEO competitors.</li>
          <li><strong>#6+ avg</strong> — you&apos;re getting cited but rarely trusted first. Backlinks from authoritative sources (news, industry publications) tend to move this fastest.</li>
        </ul>
        <p className="mt-3"><strong>Per-platform gotchas:</strong></p>
        <ul>
          <li><strong>Perplexity</strong> almost always cites sources (that&apos;s their product) — expect high mention counts here but relatively less signal about your brand strength.</li>
          <li><strong>ChatGPT / Claude</strong> may mention you in prose without citing a URL — the answer text search matters more than the sources list for these.</li>
          <li><strong>Gemini</strong> tends to cite far more sources per answer (10–20+ vs ChatGPT&apos;s 3–5) so a position of #8 in Gemini is not the same as #8 in ChatGPT.</li>
        </ul>
        <p className="mt-3"><strong>Cost profile:</strong> ~$0.01–0.02 per prompt × model. A client with 20 prompts × 4 platforms = 80 calls/month ≈ $0.80–$1.60. Prompts are hard-capped at 20 per client to keep this bounded.</p>
      </>
    ),
  },

  {
    id: "brand-mentions",
    title: "Brand Mentions",
    lead: "Live feed of Google AI Overview + ChatGPT answers where DFS observed your domain being cited by a real user's query.",
    what: (
      <>
        <p>The counterpart to GEO. Instead of firing <em>your</em> prompts at LLMs, this taps into DataForSEO&apos;s crawl of real LLM-answer inventory — every question a real user asked, every source the AI cited in reply — and filters for rows where your domain appears in the sources list. Think of it as &quot;Google Search Console for LLM answers&quot;: passive discovery of who is being pointed at you.</p>
        <p className="mt-2">Two DFS platforms supported:</p>
        <ul>
          <li><strong>Google AI Overviews</strong> — worldwide coverage, uses the client&apos;s country location code (UK by default).</li>
          <li><strong>ChatGPT</strong> — <em>US/English only</em> per DFS docs. If your client is UK-only they may see 0 ChatGPT mentions even when Google AI shows plenty — that&apos;s the coverage limit, not a real absence.</li>
        </ul>
        <p className="mt-3"><strong>Every mention row shows:</strong></p>
        <ul>
          <li><strong>Question</strong> — the exact prompt a user asked.</li>
          <li><strong>Answer excerpt</strong> — the first ~500 chars of the LLM&apos;s reply (expandable to full answer).</li>
          <li><strong>Cited page</strong> — which of your URLs was pointed at, with a direct link.</li>
          <li><strong>Your position among sources</strong> — 1-based rank in the citation list; #1 = top-cited by the LLM.</li>
          <li><strong>AI search volume</strong> — DFS&apos;s estimate of how often this specific query is asked. High volume × your citation = leads.</li>
          <li><strong>Observed timestamp</strong> — when DFS last saw this mention cluster. Rows are clusters, not individual events, so this is &quot;last observed&quot; not &quot;first seen&quot;.</li>
        </ul>
        <p className="mt-3"><strong>Discovery velocity chart</strong> — a stacked bar chart of new citations bucketed by first-seen week (last 12 weeks). Google AI = blue, ChatGPT = green. Zero-bar weeks are preserved so you can see cadence.</p>
      </>
    ),
    when: (
      <>
        <strong>Automatic:</strong> monthly cron on the 1st @ 10:30 UTC alongside GEO, rank tracking, and the digest. Silently no-ops for clients without the <span className="font-mono-tight">brand_mentions_enabled</span> flag — zero cost when opted out.<br />
        <strong>Manual:</strong> opening the tab triggers a live DFS call (or serves a cached response), and the &quot;Refresh&quot; button forces a live re-fetch.<br />
        <strong>Cache:</strong> 6 hours per (domain, platform, limit) tuple — repeated tab visits within 6h don&apos;t re-hit DFS.
      </>
    ),
    stored: "brand_mentions_snapshots collection — one doc per monthly cron run per client with rows, per-platform totals, aggregate, weekly history, cost. The tab UI reads live from DFS by default; snapshots power the AI Insights briefing month-over-month deltas.",
    good: (
      <>
        <p><strong>Reading the KPI strip:</strong></p>
        <ul>
          <li><strong>&quot;Mentions · last 7d&quot;</strong> — how many rows in the current window. Big shifts week-to-week usually signal a viral moment, a competitor drop-off, or DFS index changes.</li>
          <li><strong>&quot;Google AI Overviews · lifetime&quot;</strong> — total citations DFS has for this domain across their whole inventory. This is the &quot;authority&quot; signal — the bigger the number, the more embedded you are in LLM training/retrieval.</li>
          <li><strong>&quot;ChatGPT · lifetime&quot;</strong> — same metric, US/English restricted. Often 5–20× smaller than Google AI even for US brands.</li>
          <li><strong>&quot;Avg AI search volume&quot;</strong> — weighted demand across the returned prompts. Higher = you&apos;re being cited on questions with more human interest.</li>
        </ul>
        <p className="mt-3"><strong>Reading the top-cited pages list:</strong></p>
        <ul>
          <li>Pages with the highest count are your &quot;LLM SEO heroes&quot; — often long-form guides, glossaries, or how-tos. Refresh these first when they age out — losing a top-cited page is expensive.</li>
          <li>Watch for a page that <em>drops</em> from the top list month over month (visible in the AI Insights briefing) — usually means the content is going stale or a competitor published something better.</li>
        </ul>
        <p className="mt-3"><strong>Reading the discovery chart:</strong></p>
        <ul>
          <li><strong>Rising bars over 3+ weeks</strong> — you&apos;re earning fresh citations. Correlate with recent content publishes or link builds to attribute cause.</li>
          <li><strong>Flat / declining bars</strong> — the LLM index is stable but you&apos;re not earning <em>new</em> mentions. Content strategy or PR is the lever.</li>
          <li><strong>Zero bars for 4+ weeks</strong> — you&apos;ve stalled in the AI-answer ecosystem. Not fatal for clients with strong lifetime numbers, but a leading indicator to watch.</li>
        </ul>
        <p className="mt-3"><strong>Cost profile:</strong> ~$0.10 per platform per fetch + ~$0.001 per row. A monthly snapshot with both platforms and 25 rows each ≈ $0.25 per client per month. Manual refreshes cost the same. 10 clients / month = $30/year; 50 clients / month = $150/year.</p>
      </>
    ),
  },

  {
    id: "reports-digests",
    title: "Reports & Digests",
    lead: "Branded PDF report + Mailgun email digest, sent monthly to each client's report_email.",
    what: (
      <>
        <p>Every 1st of the month, for each client with a <span className="font-mono-tight">report_email</span> configured:</p>
        <ol>
          <li>PDF is regenerated fresh (cover with AI summary + logo, rank tables, backlink stats, GA4/GSC panels, CWV, competitor comparison).</li>
          <li>Mailgun sends the PDF as an attachment. Multiple recipients are supported — separated by commas, each Bcc&apos;d so recipients can&apos;t see each other.</li>
          <li>The greeting is personalised with <span className="font-mono-tight">client_first_name</span> if set (&quot;Hi Sarah,&quot;), otherwise &quot;Hi there,&quot;.</li>
        </ol>
      </>
    ),
    when: "1st of every month at 10:30 UTC. Manual trigger: POST /api/scheduler/run-monthly (admin only) or the 'Send digest now' button on any client page.",
    stored: "digest_logs collection tracks every attempt (sent / failed / skipped) with timestamp, recipients, and error message if any.",
    good: (
      <ul>
        <li><strong>Open rate:</strong> 60%+ is excellent for a monthly client report — these are people who <em>want</em> the email.</li>
        <li><strong>Reply rate:</strong> aim for 15–25% replies month over month — a proxy for whether the client actually reads it.</li>
        <li><strong>PDF page count:</strong> 8–14 pages is the sweet spot. Under 6 feels thin; over 20 stops being read.</li>
      </ul>
    ),
  },

  {
    id: "jobs",
    title: "Scheduled Jobs",
    lead: "Every background cron the platform runs.",
    body: (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#e2e8f0]">
              <th className="py-2 pr-4">Job</th>
              <th className="py-2 pr-4">Schedule</th>
              <th className="py-2">What it does</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-[#edf2f7]">
              <td className="py-2 pr-4 font-mono-tight font-semibold">weekly_rank_check</td>
              <td className="py-2 pr-4">Mon 09:00 UTC</td>
              <td className="py-2">Full portfolio SERP crawl. Writes rank_snapshots per client.</td>
            </tr>
            <tr className="border-b border-[#edf2f7]">
              <td className="py-2 pr-4 font-mono-tight font-semibold">weekly_pagespeed</td>
              <td className="py-2 pr-4">Tue 09:00 UTC</td>
              <td className="py-2">Runs PageSpeed Insights (mobile + desktop) on the top-10 GSC pages of every connected client.</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono-tight font-semibold">monthly_job</td>
              <td className="py-2 pr-4">1st 10:30 UTC</td>
              <td className="py-2">Rank check → competitor comparison → Mailgun digest → AI briefing. Full month-end cycle.</td>
            </tr>
          </tbody>
        </table>
        <p className="mt-3 text-[11px] text-[#a0aec0]">
          Live scheduler state (next fire time, running/paused, last-fired) is on the public <a href="/api/status" className="text-[#2c7a7b] underline">/api/status</a> page.
        </p>
      </div>
    ),
  },

  {
    id: "glossary",
    title: "Glossary",
    lead: "Quick lookup — the acronyms and jargon that appear across the app.",
    body: (
      <dl>
        <dt><strong>CID</strong></dt>
        <dd>Customer ID — Google&apos;s internal identifier for a Google Business Profile. Extracted from the <span className="font-mono-tight">?cid=…</span> parameter on a Google Maps URL. Most precise way to match a client&apos;s GBP to Local Pack results.</dd>
        <dt><strong>CTR</strong></dt>
        <dd>Click-Through Rate — clicks divided by impressions, shown as a percentage. In GSC this measures organic search CTR.</dd>
        <dt><strong>CWV</strong></dt>
        <dd>Core Web Vitals — Google&apos;s trio of user-experience metrics (LCP, INP, CLS) used as a ranking signal.</dd>
        <dt><strong>DA / PA</strong></dt>
        <dd>Domain Authority / Page Authority — Moz&apos;s 0–100 authority scores based on their backlink index.</dd>
        <dt><strong>DFS</strong></dt>
        <dd>DataForSEO — the primary data provider for SERP, keyword, backlink, and competitor data.</dd>
        <dt><strong>DR</strong></dt>
        <dd>Domain Rating — Ahrefs&apos; equivalent of Moz DA (not currently integrated; DFS Domain Rank is the closest equivalent in this dashboard).</dd>
        <dt><strong>GBP</strong></dt>
        <dd>Google Business Profile (formerly Google My Business) — the free listing that appears in Google Maps and the Local Pack.</dd>
        <dt><strong>GSC</strong></dt>
        <dd>Google Search Console — Google&apos;s free tool that reports how your site performs in organic search.</dd>
        <dt><strong>KD</strong></dt>
        <dd>Keyword Difficulty — a 0–100 score estimating how hard it is to rank for a keyword. Based on the backlink profile of the current top-10 results.</dd>
        <dt><strong>LCP / INP / CLS</strong></dt>
        <dd>Largest Contentful Paint (load speed), Interaction to Next Paint (responsiveness), Cumulative Layout Shift (visual stability) — the three Core Web Vitals.</dd>
        <dt><strong>Local Finder</strong></dt>
        <dd>The full list of local businesses that appears when you click &quot;More places&quot; on the Local Pack. Larger index than the Local Pack, useful for coverage analysis.</dd>
        <dt><strong>Local Pack</strong></dt>
        <dd>The 3-business map box that appears above organic results for local-intent searches. The single highest-value real estate in local SEO.</dd>
        <dt><strong>PBN</strong></dt>
        <dd>Private Blog Network — a group of low-quality sites owned by one person, used to build fake backlinks. Detected by low Ref-IPs / Ref-Domains ratio. Google penalises.</dd>
        <dt><strong>SERP</strong></dt>
        <dd>Search Engine Results Page — the page of results Google shows for a query.</dd>
        <dt><strong>SoV</strong></dt>
        <dd>Share of Voice — the percentage of tracked keywords where your domain ranks higher than a specific competitor. A portfolio-level competitive score.</dd>
        <dt><strong>Striking Distance</strong></dt>
        <dd>Queries where you rank positions 4–15 — close enough to top-3 that a small on-page tweak can move them up. Highest-ROI content optimisation target.</dd>
        <dt><strong>TTFB / FCP / TBT</strong></dt>
        <dd>Time to First Byte, First Contentful Paint, Total Blocking Time — secondary PageSpeed metrics that feed into the overall Performance score.</dd>
      </dl>
    ),
  },
];

/* ─────────────────────────── COMPONENTS ─────────────────────────── */

function FactCard({ icon: Icon, label, children }) {
  return (
    <div className="rounded-xl border border-[#edf2f7] bg-[#f7fafc] p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="h-6 w-6 rounded-lg bg-[#4fd1c5]/15 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
          <Icon className="h-3 w-3" strokeWidth={2.4} />
        </span>
        <span className="text-[10px] uppercase tracking-widest text-[#4a5568] font-bold">{label}</span>
      </div>
      <div className="text-sm text-[#4a5568] leading-relaxed prose-kb">{children}</div>
    </div>
  );
}

function SectionBlock({ section }) {
  return (
    <section id={section.id} className="scroll-mt-24" data-testid={`kb-section-${section.id}`}>
      <div className="card-soft p-6 sm:p-7">
        <div className="mb-4">
          <h2 className="font-display text-2xl text-[#1a202c]">{section.title}</h2>
          {section.lead && <p className="text-sm text-[#718096] mt-1">{section.lead}</p>}
        </div>
        {section.body && <div className="prose-kb text-sm text-[#4a5568] leading-relaxed">{section.body}</div>}
        {(section.what || section.when || section.stored || section.good) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {section.what && <FactCard icon={Info} label="What it measures">{section.what}</FactCard>}
            {section.when && <FactCard icon={Clock} label="When it's crawled">{section.when}</FactCard>}
            {section.stored && <FactCard icon={Database} label="Where it's stored">{section.stored}</FactCard>}
            {section.good && <FactCard icon={Target} label="What good looks like">{section.good}</FactCard>}
          </div>
        )}
        {section.caveat && (
          <div className="mt-4 rounded-xl border border-[#fbbf24]/40 bg-[#fef3c7] px-3 py-2.5 text-[12px] text-[#78350f]">
            <strong>Heads up:</strong> {section.caveat}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────── PAGE ─────────────────────────── */

export default function KnowledgeBase() {
  const [query, setQuery] = useState("");
  const trimmed = query.trim().toLowerCase();

  // Filter sections by title / lead / body-text-string. React nodes are flattened
  // to a rough string for search — good enough for the ~15 sections we have.
  const filtered = useMemo(() => {
    if (!trimmed) return SECTIONS;
    const flatten = (node) => {
      if (node == null || typeof node === "boolean") return "";
      if (typeof node === "string" || typeof node === "number") return String(node);
      if (Array.isArray(node)) return node.map(flatten).join(" ");
      if (node.props?.children) return flatten(node.props.children);
      return "";
    };
    return SECTIONS.filter((s) => {
      const hay = [
        s.title, s.lead,
        flatten(s.body), flatten(s.what), flatten(s.when), flatten(s.stored), flatten(s.good),
        s.caveat,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(trimmed);
    });
  }, [trimmed]);

  return (
    <div className="space-y-5 pt-2" data-testid="knowledge-base-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Pages / Knowledge Base</div>
        <div className="flex items-start justify-between gap-4 mt-1">
          <div>
            <h1 className="font-display text-4xl text-[#1a202c] flex items-center gap-3">
              <BookOpen className="h-7 w-7 text-[#4fd1c5]" strokeWidth={2} />
              Knowledge Base
            </h1>
            <p className="text-sm text-[#718096] mt-1 max-w-2xl">
              Answers to every &quot;what does this metric mean?&quot; and &quot;when does this refresh?&quot; question — for the whole team.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <SearchIcon className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-[#a0aec0]" strokeWidth={2.2} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the knowledge base…"
          data-testid="kb-search"
          className="w-full bg-white border border-[#e2e8f0] rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Left rail — sticky jump-to */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold px-3 mb-2">
              On this page
            </div>
            <nav className="space-y-0.5" data-testid="kb-toc">
              {filtered.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`kb-toc-${s.id}`}
                  className="block px-3 py-1.5 text-sm text-[#4a5568] hover:text-[#2c7a7b] hover:bg-white rounded-lg transition-colors"
                >
                  {s.title}
                </a>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-1.5 text-sm text-[#a0aec0]">No matches</div>
              )}
            </nav>
          </div>
        </aside>

        {/* Right content */}
        <div className="space-y-5 min-w-0">
          {filtered.map((s) => <SectionBlock key={s.id} section={s} />)}
          {filtered.length === 0 && (
            <div className="card-soft p-10 text-center text-sm text-[#a0aec0]" data-testid="kb-empty">
              No sections match &quot;{query}&quot;. Try a different search term.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
